import { Box, ButtonBase, Menu, MenuItem, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { type ReactNode, useCallback, useState } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { ALPHA, BADGE, FONT_SIZE, INK, LETTER_SPACING, LEVEL_METER, MOTION, SHADOW, TOUCH_TARGET_MIN } from "@/theme/tokens";
import { portalOverlayProps } from "../Popups/portalOverlay";
import SystemButton from "../SystemButton/SystemButton";

export type LevelOption<T> = {
	value: T;
	/** Short label — shown in the pill and the menu row. Caller translates it. */
	label: string;
	/** One line on what this level does, shown as the pill's tooltip and under the menu label. */
	hint: string;
};

type Props<T> = {
	/** The scale, ordered lowest → highest: the position of `value` is what the meter lights up. */
	options: LevelOption<T>[];
	value: T;
	onChange: (value: T) => void;
	/** Accessible name of the whole control ("Answer depth", "Analysis effort", …). */
	ariaLabel: string;
	/** ID of persistent guidance associated with the trigger. */
	ariaDescribedBy?: string;
	disabled?: boolean;
	/** Opens the same choices from a quiet header icon instead of a labeled pill. */
	triggerIcon?: ReactNode;
	dataTestId?: string;
};

/** The instrument glyph: `filledCount` rising bars lit in `tone`, the rest present but off. */
const LevelMeter = <T,>({ options, filledCount, tone }: { options: LevelOption<T>[]; filledCount: number; tone: string }) => (
	<Box aria-hidden sx={{ display: "flex", alignItems: "flex-end", flexShrink: 0, gap: `${LEVEL_METER.BAR_GAP}px` }}>
		{options.map((option, index) => (
			<Box
				key={option.label}
				sx={{
					width: `${LEVEL_METER.BAR_WIDTH}px`,
					height: `${LEVEL_METER.BAR_MIN_HEIGHT + index * LEVEL_METER.BAR_STEP}px`,
					borderRadius: `${LEVEL_METER.BAR_RADIUS}px`,
					backgroundColor: index < filledCount ? tone : alpha(INK.SECONDARY, ALPHA.METER_EMPTY_BAR),
					transition: `background-color ${MOTION.DURATION.CONTROL}ms ease`,
				}}
			/>
		))}
	</Box>
);

/** Compact ordered-level picker that grows with text and opens a viewport-bounded menu. */
export const InlineLevelPicker = <T,>({ options, value, onChange, ariaLabel, ariaDescribedBy, disabled, triggerIcon, dataTestId }: Props<T>) => {
	const theme = useTheme();
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const isMenuOpen = menuAnchor !== null;

	const selectedIndex = options.findIndex((option) => option.value === value);
	// A value outside the scale would silently render an empty meter — the caller's option list is wrong.
	if (selectedIndex === -1) throw new Error("InlineLevelPicker received a value that is not one of its options");
	const selected = options[selectedIndex];

	const handlePick = useCallback(
		(picked: T) => {
			setMenuAnchor(null);
			onChange(picked);
		},
		[onChange],
	);

	return (
		<>
			<InHouseTooltip title={`${ariaLabel}: ${selected.label} · ${selected.hint}`} arrow placement="top">
				<Box component="span" sx={{ display: "inline-flex", minWidth: 0, maxWidth: "100%" }}>
					{triggerIcon !== undefined ? (
						<SystemButton
							iconButton
							buttonVariant="borderless"
							disabled={disabled}
							onClick={(event) => setMenuAnchor(event.currentTarget)}
							aria-haspopup="menu"
							aria-expanded={isMenuOpen}
							aria-label={`${ariaLabel}: ${selected.label}`}
							aria-describedby={ariaDescribedBy}
							data-testid={dataTestId}
							touchTarget
						>
							{triggerIcon}
						</SystemButton>
					) : (
						<ButtonBase
							data-testid={dataTestId}
							disabled={disabled}
							onClick={(event) => setMenuAnchor(event.currentTarget)}
							aria-haspopup="menu"
							aria-expanded={isMenuOpen}
							aria-label={`${ariaLabel}: ${selected.label}`}
							aria-describedby={ariaDescribedBy}
							sx={{
								minHeight: `${LEVEL_METER.PILL_HEIGHT}px`,
								"@media (pointer: coarse)": {
									minHeight: `${TOUCH_TARGET_MIN}px`,
								},
								height: "auto",
								minWidth: 0,
								maxWidth: "100%",
								px: 0.75,
								py: 0.5,
								gap: 0.625,
								borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
								color: theme.palette.primary.dark,
								backgroundColor: alpha(theme.palette.primary.main, isMenuOpen ? ALPHA.CONTROL_TINT_HOVER : ALPHA.SELECTED_OVERLAY),
								transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ease, transform ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
								"&:active": { transform: "scale(0.97)" },
								"&.Mui-disabled": { opacity: 0.5 },
								"&:focus-visible": { outline: `2px solid ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`, outlineOffset: 1 },
								// Hover tint only where hovering is real — on touch the pill would otherwise stay "lit" after a tap.
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) },
								},
								"@media (prefers-reduced-motion: reduce)": { "&:active": { transform: "none" } },
							}}
						>
							<LevelMeter options={options} filledCount={selectedIndex + 1} tone={theme.palette.primary.main} />
							<Typography
								sx={{
									fontSize: FONT_SIZE.LABEL_XS,
									fontWeight: 600,
									lineHeight: 1,
									letterSpacing: LETTER_SPACING.LABEL,
									textTransform: "uppercase",
									minWidth: 0,
									whiteSpace: "normal",
									overflowWrap: "anywhere",
								}}
							>
								{selected.label}
							</Typography>
						</ButtonBase>
					)}
				</Box>
			</InHouseTooltip>
			<Menu
				anchorEl={menuAnchor}
				open={isMenuOpen}
				onClose={() => setMenuAnchor(null)}
				anchorOrigin={{ vertical: triggerIcon !== undefined ? "bottom" : "top", horizontal: "left" }}
				transformOrigin={{ vertical: triggerIcon !== undefined ? "top" : "bottom", horizontal: "left" }}
				slotProps={{
					paper: {
						...portalOverlayProps("inline-level-picker"),
						sx: {
							maxWidth: `min(${LEVEL_METER.MENU_MAX_WIDTH}px, calc(100vw - ${theme.spacing(4)}))`,
							overflowWrap: "anywhere",
							boxShadow: SHADOW.POPOVER_FLOAT,
							borderRadius: 2,
						},
					},
					list: { dense: true, "aria-label": ariaLabel },
				}}
			>
				{options.map((option, index) => (
					<MenuItem
						key={option.label}
						role="menuitemradio"
						aria-checked={index === selectedIndex}
						selected={index === selectedIndex}
						onClick={() => handlePick(option.value)}
						data-testid={dataTestId ? `${dataTestId}-option-${String(option.value)}` : undefined}
						sx={{ gap: 1.25, alignItems: "center", py: 0.75, whiteSpace: "normal" }}
					>
						<LevelMeter options={options} filledCount={index + 1} tone={index === selectedIndex ? theme.palette.primary.main : INK.MUTED} />
						<Box sx={{ minWidth: 0 }}>
							<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: index === selectedIndex ? 600 : 500, lineHeight: 1.3 }}>{option.label}</Typography>
							<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, lineHeight: 1.35 }}>{option.hint}</Typography>
						</Box>
					</MenuItem>
				))}
			</Menu>
		</>
	);
};
